/**
 * Pure label/readout logic for the on-map annotation label layer (ADR-034
 * Inc 5; feature-spec §7 / §12 / D12). Computes, for each annotation, the text
 * to show on the map (the operator's text label + the measurement readout) and
 * where to anchor it, and builds the MapLibre symbol-layer source. Pure (no
 * map / no GL) so it's CI-testable; the symbol layer that renders it is
 * verified in the deferred §5.11 visual pass.
 *
 * The on-map text combines, separated by a newline:
 *   - the operator's text ``label.text`` (when non-empty), then
 *   - the measurement readout (when ``label.show_measurement`` — distance +
 *     azimuth for a line, radius for a circle, area + perimeter for a polygon,
 *     coordinates for a marker), formatted per the operator's units — but ONLY
 *     in ``on_map`` display mode. In ``hover`` mode the measurement is omitted
 *     here and shown in a hover tooltip instead
 *     (:func:`annotationHoverMeasurementText`, #8); the text label still shows.
 *
 * Cross-references:
 *   - ADR-034; feature-spec §7 (labels) / §9 (units) / D12.
 *   - @/annotations/measure (the geodesic readouts).
 */
import type { Feature, FeatureCollection, Point } from "geojson";
import type { CoordinateFormat, DistanceUnit } from "@/auth/session-types";
import type { Annotation } from "@/annotations/use-annotations";
import { formatDistance } from "@/units/format-distance";
import {
  circleAreaM2,
  circleCircumferenceM,
  formatArea,
  formatAzimuth,
  lineMeasurement,
  markerMeasurement,
  polygonMeasurement,
} from "@/annotations/measure";

/** A measurement key the readout can show (the API ``measurements`` element). */
export type MeasurementKey = NonNullable<Annotation["label"]["measurements"]>[number];

/** The measurements available per kind, in canonical readout order. */
export const MEASUREMENTS_BY_KIND: Record<Annotation["kind"], readonly MeasurementKey[]> = {
  line: ["length", "azimuth"],
  circle: ["area", "radius", "circumference"],
  polygon: ["area", "perimeter"],
  marker: ["location"],
};

/** Metres per degree of latitude (WGS84 mean) — for the circle-top label anchor. */
const METRES_PER_DEG_LAT = 111_320;

/** The bottom-centre of a ring's bounding box — the polygon/area label sits
 *  below the shape, horizontally centred (#6 — operator request 2026-06-12). */
function ringBottomCentre(ring: readonly number[][]): [number, number] {
  let minLat = ring[0][1];
  let minLng = ring[0][0];
  let maxLng = ring[0][0];
  for (const [lng, lat] of ring) {
    if (lat < minLat) minLat = lat;
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
  }
  return [(minLng + maxLng) / 2, minLat];
}

/** The formatted value for one measurement key, or ``null`` if it doesn't apply
 *  to this annotation. Distances that are otherwise indistinguishable carry a
 *  prefix (``R`` radius, ``C`` circumference). */
function formatMeasurement(
  key: MeasurementKey,
  annotation: Annotation,
  distanceUnit: DistanceUnit,
  coordinateFormat: CoordinateFormat,
): string | null {
  const geometry = annotation.geometry;
  const radius = annotation.radius_m;
  switch (key) {
    case "length":
      return geometry.type === "LineString"
        ? formatDistance(
            lineMeasurement(geometry.coordinates as number[][], distanceUnit).distanceM,
            distanceUnit,
          )
        : null;
    case "azimuth":
      return geometry.type === "LineString"
        ? formatAzimuth(
            lineMeasurement(geometry.coordinates as number[][], distanceUnit).azimuthDeg,
          )
        : null;
    case "area":
      if (annotation.kind === "circle" && radius !== null && radius !== undefined) {
        return formatArea(circleAreaM2(radius), distanceUnit);
      }
      return geometry.type === "Polygon"
        ? formatArea(
            polygonMeasurement((geometry.coordinates as number[][][])[0], distanceUnit).areaM2,
            distanceUnit,
          )
        : null;
    case "radius":
      return radius !== null && radius !== undefined
        ? `R ${formatDistance(radius, distanceUnit)}`
        : null;
    case "circumference":
      return radius !== null && radius !== undefined
        ? `C ${formatDistance(circleCircumferenceM(radius), distanceUnit)}`
        : null;
    case "perimeter":
      return geometry.type === "Polygon"
        ? formatDistance(
            polygonMeasurement((geometry.coordinates as number[][][])[0], distanceUnit).perimeterM,
            distanceUnit,
          )
        : null;
    case "location":
      return geometry.type === "Point"
        ? markerMeasurement(geometry.coordinates as number[], coordinateFormat).label
        : null;
    default:
      return null;
  }
}

/* Circle radius line (REQ-UI-134). A dotted segment from the centre to the
 * due-east edge, labelled with the radius, so an operator can read a circle's
 * size straight off the map instead of selecting it and reading a panel.
 *
 * Visibility follows the circle's OWN measurement selection rather than a
 * global switch: the line draws when that circle would have shown its radius
 * in the on-map readout anyway. That keeps one control — the per-shape
 * measurement checkboxes in Entity Details — governing both the text and the
 * line, so an operator who unticks "radius" gets the shape decluttered rather
 * than half-decluttered.
 *
 * The radius text MOVES onto the line rather than being duplicated: the main
 * circle label drops its radius component while the line is drawn
 * (annotation-label.measurementText). Two copies of the same number a
 * centimetre apart is worse than either placement alone.
 *
 * The outer end doubles as the circle's resize handle under the Edit tool. */

/**
 * Whether this annotation should render a radius line (REQ-UI-134) — true
 * only for a circle whose own measurement selection includes ``radius`` and
 * which shows its readout on the map.
 *
 * Lives here rather than with the render builders because it is a
 * measurement-display decision, and because :func:`measurementText` needs it
 * to drop the duplicate radius from the main label.
 *
 * ``measurements: null`` means "the defaults for this kind", and the circle
 * defaults (:data:`MEASUREMENTS_BY_KIND`) include radius — so a circle drawn
 * with no explicit configuration does show its radius line.
 */
export function circleShowsRadiusLine(annotation: Annotation): boolean {
  if (annotation.kind !== "circle" || annotation.radius_m == null) {
    return false;
  }
  if (!annotation.label.show_measurement) {
    return false;
  }
  // ``hover`` mode deliberately keeps this circle's readout off the map; a
  // labelled radius line drawn anyway would contradict that choice.
  if ((annotation.label.measurement_display ?? "on_map") !== "on_map") {
    return false;
  }
  const selected = annotation.label.measurements ?? MEASUREMENTS_BY_KIND.circle;
  return selected.includes("radius");
}

/**
 * The measurement readout — the operator-selected measurements
 * (``label.measurements``; ``null`` = all available for the kind) in canonical
 * per-kind order, **one per line** (#11 — no mid-readout wrap). ``null`` when
 * nothing applies.
 */
function measurementText(
  annotation: Annotation,
  distanceUnit: DistanceUnit,
  coordinateFormat: CoordinateFormat,
): string | null {
  const selected = new Set<MeasurementKey>(
    annotation.label.measurements ?? MEASUREMENTS_BY_KIND[annotation.kind],
  );
  // The radius line carries its own label, so the main readout drops radius
  // while that line is drawn (REQ-UI-134). Two copies of the same number a
  // centimetre apart is worse than either placement alone.
  const radiusOnLine = circleShowsRadiusLine(annotation);
  const parts: string[] = [];
  for (const key of MEASUREMENTS_BY_KIND[annotation.kind]) {
    if (!selected.has(key)) {
      continue;
    }
    if (radiusOnLine && key === "radius") {
      continue;
    }
    const value = formatMeasurement(key, annotation, distanceUnit, coordinateFormat);
    if (value !== null) {
      parts.push(value);
    }
  }
  return parts.length > 0 ? parts.join("\n") : null;
}

/**
 * The on-map text for an annotation: the operator's text label (when non-empty)
 * + the measurement readout (when ``show_measurement``), newline-separated.
 * ``null`` when there's nothing to show.
 */
export function annotationDisplayText(
  annotation: Annotation,
  distanceUnit: DistanceUnit,
  coordinateFormat: CoordinateFormat,
): string | null {
  const parts: string[] = [];
  const labelText = annotation.label.text.trim();
  if (labelText.length > 0) {
    parts.push(labelText);
  }
  // The measurement readout shows on the map only in ``on_map`` display mode; in
  // ``hover`` mode it's shown in a hover tooltip instead (#8 — see
  // annotationHoverMeasurementText). The text label still shows here either way.
  const onMap = (annotation.label.measurement_display ?? "on_map") === "on_map";
  if (annotation.label.show_measurement && onMap) {
    const measurement = measurementText(annotation, distanceUnit, coordinateFormat);
    if (measurement !== null) {
      parts.push(measurement);
    }
  }
  return parts.length > 0 ? parts.join("\n") : null;
}

/**
 * The measurement readout to show in the **hover tooltip** (#8): the same readout
 * as on-map mode, but only for an annotation whose ``measurement_display`` is
 * ``"hover"`` (and ``show_measurement`` is on). ``null`` otherwise — the caller
 * shows no tooltip. The on-map text label is handled separately
 * (:func:`annotationDisplayText`) and is always visible regardless of mode.
 */
export function annotationHoverMeasurementText(
  annotation: Annotation,
  distanceUnit: DistanceUnit,
  coordinateFormat: CoordinateFormat,
): string | null {
  if (!annotation.label.show_measurement) {
    return null;
  }
  if ((annotation.label.measurement_display ?? "on_map") !== "hover") {
    return null;
  }
  return measurementText(annotation, distanceUnit, coordinateFormat);
}

/**
 * The full copyable text for an annotation (#9 — the right-click "Copy
 * measurements" action): the operator's text label + the measurement readout,
 * newline-separated, **regardless** of the ``on_map`` / ``hover`` display mode
 * (copy should yield the measurements even when they live in a hover tooltip
 * rather than on the map). ``""`` when there's nothing to copy.
 */
export function annotationCopyText(
  annotation: Annotation,
  distanceUnit: DistanceUnit,
  coordinateFormat: CoordinateFormat,
): string {
  const parts: string[] = [];
  const labelText = annotation.label.text.trim();
  if (labelText.length > 0) {
    parts.push(labelText);
  }
  if (annotation.label.show_measurement) {
    const measurement = measurementText(annotation, distanceUnit, coordinateFormat);
    if (measurement !== null) {
      parts.push(measurement);
    }
  }
  return parts.join("\n");
}

/**
 * The ``[lon, lat]`` anchor for an annotation's label, placed at the BOTTOM of
 * the shape so the label hangs below it (#5/#6 — operator request 2026-06-12): a
 * line's END point (below the end arrowhead), a circle's/polygon's bottom edge
 * (horizontally centred), or the marker point. :func:`labelPlacement` then offsets
 * the text below this anchor.
 */
export function annotationAnchor(annotation: Annotation): [number, number] {
  const geometry = annotation.geometry;
  if (annotation.kind === "line" && geometry.type === "LineString") {
    const coordinates = geometry.coordinates as number[][];
    const last = coordinates[coordinates.length - 1];
    return [last[0], last[1]]; // the END point — label below it, clear of the arrow (#5)
  }
  if (annotation.kind === "polygon" && geometry.type === "Polygon") {
    return ringBottomCentre((geometry.coordinates as number[][][])[0]); // below, centred (#6)
  }
  if (annotation.kind === "circle" && geometry.type === "Point") {
    const coordinates = geometry.coordinates as number[];
    const radius = annotation.radius_m;
    const dLat = radius !== null && radius !== undefined ? radius / METRES_PER_DEG_LAT : 0;
    return [coordinates[0], coordinates[1] - dLat]; // bottom of the circle (#6)
  }
  // marker → the Point geometry.
  const coordinates = geometry.coordinates as number[];
  return [coordinates[0], coordinates[1]];
}

/** The MapLibre ``text-anchor`` + ``text-offset`` so a label hangs BELOW its
 *  anchor (#5/#6): every kind text-anchors at the top so the text sits under the
 *  point; the marker gets a bit more offset to clear the pin glyph. */
function labelPlacement(kind: Annotation["kind"]): {
  textAnchor: string;
  textOffset: readonly [number, number];
} {
  return kind === "marker"
    ? { textAnchor: "top", textOffset: [0, 0.8] }
    : { textAnchor: "top", textOffset: [0, 0.5] };
}

/**
 * Build the symbol-layer source: one label ``Point`` per annotation that has
 * something to show, carrying the text + the per-annotation label colour/size.
 */
export function annotationsToLabelFeatures(
  annotations: readonly Annotation[],
  distanceUnit: DistanceUnit,
  coordinateFormat: CoordinateFormat,
): FeatureCollection<Point> {
  const features: Feature<Point>[] = [];
  for (const annotation of annotations) {
    const text = annotationDisplayText(annotation, distanceUnit, coordinateFormat);
    if (text === null) {
      continue;
    }
    const placement = labelPlacement(annotation.kind);
    features.push({
      type: "Feature",
      // A stable feature id so MapLibre matches symbols across setData calls (the
      // unit-change re-render) instead of fading them out/in — #13/#14.
      id: annotation.annotation_id,
      geometry: { type: "Point", coordinates: annotationAnchor(annotation) },
      properties: {
        annotation_id: annotation.annotation_id,
        text,
        color: annotation.label.color,
        size_px: annotation.label.size_px,
        text_anchor: placement.textAnchor,
        text_offset: placement.textOffset,
      },
    });
  }
  return { type: "FeatureCollection", features };
}
